/**
 * Fait : le FIL de conversation à l'écran (lot 5.6) — les messages de la
 *   branche courante, le choix du modèle (depuis le catalogue, jamais un nom
 *   en dur), l'envoi, la réponse qui ARRIVE EN DIRECT par le canal, bloc par
 *   bloc, l'annulation par son déclencheur, la modification de SON message,
 *   la régénération en branche, et la comparaison avec estimation avant
 *   confirmation. Chaque refus du serveur s'affiche tel quel.
 *
 * Ne fait pas : elle ne décide d'aucun droit (les boutons suivent ce que
 *   l'API accepte) et ne calcule aucun coût — l'API estime, l'écran affiche.
 */

import { useEffect, useRef, useState } from 'react';

import { Annonce } from '../gabarits/Annonce.tsx';
import { Partie } from '../gabarits/Partie.tsx';
import {
  lanceLaComparaison,
  lanceUneGeneration,
  listeLeCatalogue,
  listeLesBranches,
  listeLesMessagesDuFil,
  modifieUnMessage,
  regenereEnBranche,
  type BrancheVue,
  type MessageVu,
  type ModeleVu,
} from './api-fil.ts';
import { Comparateur } from './Comparateur.tsx';
import { ComposerDuFil } from './ComposerDuFil.tsx';
import { ouvreLeCanalDuFil, type ConnexionDuFil } from './canal-du-fil.ts';
import { ecouteDuFluxAffiche } from './flux-du-fil.ts';
import { useFraicheur } from './fraicheur.ts';
import { MessagesDuFil, type EditionEnCours, type GenerationVisible } from './MessagesDuFil.tsx';
import { SelecteurDeBranche } from './SelecteurDeBranche.tsx';

export function PageFil({
  projetId,
  filId,
}: {
  readonly projetId: string;
  readonly filId: string;
}) {
  const [branches, setBranches] = useState<BrancheVue[]>([]);
  const [brancheId, setBrancheId] = useState<string | null>(null);
  const [messages, setMessages] = useState<MessageVu[]>([]);
  const [modeles, setModeles] = useState<ModeleVu[]>([]);
  const [modeleId, setModeleId] = useState('');
  const [texte, setTexte] = useState('');
  const [enCours, setEnCours] = useState<GenerationVisible | null>(null);
  const [enEdition, setEnEdition] = useState<EditionEnCours | null>(null);
  const [comparateurOuvert, setComparateurOuvert] = useState(false);
  const [echec, setEchec] = useState<string | null>(null);
  const prise = useRef<ConnexionDuFil | null>(null);

  // Même garde que la fiche de projet — la porte jumelle se ferme une fois.
  const fraicheur = useFraicheur(filId);

  /** La branche RÉELLEMENT affichée : une réponse d'une autre est périmée. */
  const brancheAffichee = useRef<string | null>(null);
  brancheAffichee.current = brancheId;

  /** Recharge les messages d'une branche — fil ET branche doivent tenir. */
  async function recharge(branche: string): Promise<void> {
    const pour = fraicheur.demandee;
    const lus = await listeLesMessagesDuFil(pour, branche);

    if (!fraicheur.encoreValable(pour)) return;
    if (brancheAffichee.current !== branche) return;
    if (lus.bon) setMessages(lus.valeur.messages);
  }

  /** Change la branche montrée : le flux de l'ancienne ne nous concerne plus. */
  function bascule(branche: string): void {
    setBrancheId(branche);
    setEnCours(null);
  }

  useEffect(() => {
    setEchec(null);
    setEnCours(null);

    void (async () => {
      const pour = fraicheur.demandee;
      const [sesBranches, catalogue] = await Promise.all([
        listeLesBranches(pour),
        listeLeCatalogue(),
      ]);

      // Gardé AUTANT que `recharge` : les branches d'un fil quitté armaient
      // sinon le composer d'un autre (troisième audit, phase 5).
      if (!fraicheur.encoreValable(pour)) return;

      if (!sesBranches.bon) {
        setEchec(sesBranches.erreur.message);
        return;
      }

      setBranches(sesBranches.valeur.branches);
      const initiale = sesBranches.valeur.branches[0]?.id ?? null;
      setBrancheId(initiale);
      if (initiale !== null) await recharge(initiale);

      if (catalogue.bon && fraicheur.encoreValable(pour)) {
        setModeles(catalogue.valeur.modeles);
        setModeleId(catalogue.valeur.modeles[0]?.id ?? '');
      }
    })();

    // UNE connexion par écran : elle porte le flux ET l'annulation. Le canal
    // reçoit le projet ENTIER — le filtre ne garde que la branche montrée
    // (bloquant du 4e audit : sans lui, la réponse d'un autre fil s'affichait
    // ici et le composer gelait sur toute génération du projet).
    const canal = ouvreLeCanalDuFil(
      projetId,
      ecouteDuFluxAffiche({
        filId,
        brancheAffichee: () => brancheAffichee.current,
        poseLaGeneration: setEnCours,
        rechargeLaBranche: (branche) => void recharge(branche),
        montreLEchec: setEchec,
        surCoupure: () => {
          setEchec('Le lien avec l’atelier est coupé. Rechargez la page.');
        },
      }),
    );
    prise.current = canal;

    return () => {
      canal.ferme();
    };
    // `recharge` lit l'état à l'appel : le fil seul commande cet effet.
  }, [projetId, filId]);

  /** Envoie le texte : le message part, la réponse arrivera par le canal. */
  async function envoie(): Promise<void> {
    if (brancheId === null || texte.trim() === '') return;

    const issue = await lanceUneGeneration(filId, brancheId, modeleId, texte.trim());
    if (!issue.bon) {
      setEchec(issue.erreur.message);
      // Le message du membre est ÉCRIT avant la réservation : sur un refus
      // de plafond, il est en base même si rien n'est parti. Sans ce
      // rechargement, l'écran laissait croire qu'il n'existait pas — et il
      // serait pourtant facturé comme contexte au prochain appel (mineur de
      // l'audit de la phase 5). Le composer se vide AUSSI : le laisser plein
      // faisait écrire un doublon au second clic (mineur du re-audit).
      setTexte('');
      await recharge(brancheId);
      return;
    }

    setEchec(null);
    setTexte('');
    await recharge(brancheId);
  }

  /** Annule la génération en cours — le serveur vérifie le déclencheur. */
  function annule(): void {
    if (enCours !== null) prise.current?.annule(enCours.generationId);
  }

  /** Enregistre la modification de SON message. */
  async function enregistreLEdition(): Promise<void> {
    if (enEdition === null || brancheId === null) return;

    const issue = await modifieUnMessage(enEdition.id, enEdition.texte, enEdition.version);
    if (!issue.bon) {
      setEchec(issue.erreur.message);
      return;
    }

    setEnEdition(null);
    await recharge(brancheId);
  }

  /** Régénère depuis ce message : nouvelle branche, nouvelle réponse. */
  async function regenere(messageDOrigineId: string): Promise<void> {
    const issue = await regenereEnBranche(filId, messageDOrigineId, modeleId);
    if (!issue.bon) {
      setEchec(issue.erreur.message);
      return;
    }

    const sesBranches = await listeLesBranches(filId);
    if (sesBranches.bon) setBranches(sesBranches.valeur.branches);
    bascule(issue.valeur.brancheId);
    await recharge(issue.valeur.brancheId);
  }

  /** Lance la comparaison confirmée, puis suit la première branche. */
  async function compare(entreesIds: string[]): Promise<void> {
    if (brancheId === null) return;

    const issue = await lanceLaComparaison(filId, brancheId, entreesIds, texte.trim());
    setComparateurOuvert(false);
    if (!issue.bon) {
      setEchec(issue.erreur.message);
      return;
    }

    setTexte('');
    const sesBranches = await listeLesBranches(filId);
    if (sesBranches.bon) setBranches(sesBranches.valeur.branches);

    const premiere = issue.valeur.lancements[0];
    if (premiere !== undefined) {
      bascule(premiere.brancheId);
      await recharge(premiere.brancheId);
    }
  }

  return (
    <div className="empilement" data-fil={filId} data-etat="nominal">
      <p>
        <a href={`#/projets/${projetId}`}>← Revenir au projet</a>
      </p>

      {/*
       * UN SEUL CASIER — décision de Charles sur maquette, 2026-08-28 : les
       * échanges, l'annonce d'échec et le champ d'écriture partagent le bloc,
       * reliés par le rail ; le champ est la dernière station, sa pastille
       * porte le numéro suivant, et son libellé vit sur le champ lui-même.
       */}
      <Partie
        etiquette="La conversation"
        nature="travail"
        titre="Les échanges"
        aide="Vos questions et les réponses, dans l’ordre."
        data-role="partie-echanges"
      >
        <SelecteurDeBranche
          branches={branches}
          brancheId={brancheId}
          surBascule={(cible) => {
            bascule(cible);
            void recharge(cible);
          }}
        />

        <div className="corps-du-fil">
          <MessagesDuFil
            messages={messages}
            enCours={enCours}
            enEdition={enEdition}
            surEdition={setEnEdition}
            surEnregistrement={() => void enregistreLEdition()}
            surRegeneration={(messageDOrigineId) => void regenere(messageDOrigineId)}
            surAnnulation={annule}
          />

          {echec !== null && (
            <span data-role="echec" className="station-d-annonce">
              <Annonce urgence="urgent">{echec}</Annonce>
            </span>
          )}

          <div className="station-d-ecriture" data-role="partie-composer">
            <span className="numero-du-fil" aria-hidden="true">
              {(messages.at(-1)?.rang ?? -1) + (enCours === null ? 2 : 3)}
            </span>
            <ComposerDuFil
              modeles={modeles}
              modeleId={modeleId}
              texte={texte}
              generationEnCours={enCours !== null}
              pret={brancheId !== null && modeleId !== ''}
              surModele={setModeleId}
              surTexte={setTexte}
              surEnvoi={() => void envoie()}
              surComparaison={() => {
                setComparateurOuvert(true);
              }}
            />

            {comparateurOuvert && brancheId !== null && (
              <Comparateur
                filId={filId}
                brancheId={brancheId}
                texte={texte.trim()}
                modeles={modeles}
                surConfirmation={(entreesIds) => void compare(entreesIds)}
                surFermeture={() => {
                  setComparateurOuvert(false);
                }}
              />
            )}
          </div>
        </div>
      </Partie>
    </div>
  );
}
